/**
 * Composable for package search functionality
 * Handles progressive flight search, hotel availability, and other package services
 */
export const usePackageServiceSearch = () => {
  const { t } = useI18n()
  const { $toast } = useNuxtApp()
  const packageStore = usePackageStore()
  const loadingStore = useLoadingStore()
  const bookStore = useBookStore()

  /**
   * Performs progressive flight search with automatic retry logic
   */
  const performProgressiveFlightSearch = async (searchType, searchFunction, searchParams) => {
    const maxRetries = 2 // Try original maxChanges, then maxChanges + 1
    let currentMaxChanges = searchParams.maxStops || 0

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const updatedParams = {
          ...searchParams,
          maxStops: currentMaxChanges
        }

        // Update loading text to show progressive search
        if (attempt > 0) {
          const stopsText = currentMaxChanges > 1
            ? t('search.flight_search_with_stops_plural', { type: searchType, stops: currentMaxChanges })
            : t('search.flight_search_with_stops_singular', { type: searchType, stops: currentMaxChanges })
          loadingStore.loadingText = stopsText
        }

        const flights = await searchFunction(updatedParams)

        // Check if we got any results
        if (!flights || flights.length === 0) {
          // If this was our last attempt, return null to indicate no flights found
          if (attempt === maxRetries - 1) {
            return null
          }

          // Increment maxChanges for next attempt
          currentMaxChanges++
          continue
        }

        return { flights, maxChanges: currentMaxChanges }
      } catch (e) {
        // If this was our last attempt, throw the error
        if (attempt === maxRetries - 1) {
          throw e
        }

        // Increment maxChanges for next attempt
        currentMaxChanges++
      }
    }

    return null
  }

  /**
   * Checks outward flight availability with progressive search
   */
  const checkOutwardFlightAvailability = async (searchParams, payload) => {
    if (bookStore.isFlightLessBooking) {
      handleFlightLessPackage()
      return
    }

    try {
      const result = await performProgressiveFlightSearch(
        t('search.outward_flight_type'),
        packageStore.fetchOutwardFlights,
        searchParams
      )

      if (!result) {
        handleFlightLessPackage()
        return
      }

      const { flights: outwardFlights, maxChanges } = result

      // Update payload with successful maxChanges value for consistency
      if (maxChanges !== payload.maxChanges) {
        payload.maxChanges = maxChanges
      }

      // To make sure it does not fill when one flight (return/outward) is not available
      if (bookStore.isFlightLessBooking) {
        return
      }

      packageStore.isFlightLessPackage = false

      let selectedFlight = outwardFlights[0]
      if (payload.offerId && payload.outwardFlightId) {
        selectedFlight = outwardFlights.find(
          flight => flight.uid === payload.outwardFlightId
        )
        if (!selectedFlight) {
          selectedFlight = outwardFlights[0]
          // Handle offer failed service logic here if needed
        }
      }

      bookStore.outwardFlight = selectedFlight
    } catch (e) {
      handleFlightLessPackage()
      throw e
    }
  }

  /**
   * Checks return flight availability with progressive search
   */
  const checkReturnFlightAvailability = async (searchParams, payload) => {
    if (bookStore.isFlightLessBooking) {
      handleFlightLessPackage()
      return
    }

    try {
      const result = await performProgressiveFlightSearch(
        t('search.return_flight_type'),
        packageStore.fetchReturnFlights,
        searchParams
      )

      if (!result) {
        handleFlightLessPackage()
        return
      }

      const { flights: returnFlights, maxChanges } = result

      // Update payload with successful maxChanges value for consistency
      if (maxChanges !== payload.maxChanges) {
        payload.maxChanges = maxChanges
      }

      // To make sure it does not fill when one flight (return/outward) is not available
      if (bookStore.isFlightLessBooking) {
        return
      }

      packageStore.isFlightLessPackage = false

      let selectedFlight = returnFlights[0]
      if (payload.offerId && payload.returnFlightId) {
        selectedFlight = returnFlights.find(
          flight => flight.uid === payload.returnFlightId
        )
        if (!selectedFlight) {
          selectedFlight = returnFlights[0]
          // Handle offer failed service logic here if needed
        }
      }

      bookStore.returnFlight = selectedFlight
    } catch (e) {
      handleFlightLessPackage()
      throw e
    }
  }

  /**
   * Handles conversion to flight-less package
   */
  const handleFlightLessPackage = () => {
    packageStore.isFlightLessPackage = true
    bookStore.isFlightLessBooking = true

    // Clear any existing flight data to prevent inconsistencies
    bookStore.outwardFlight = null
    bookStore.returnFlight = null
  }

  /**
   * Sets up initial loading states and messages
   */
  const initializeSearch = () => {
    loadingStore.isLoading = true
    loadingStore.topLoadingText = t('search.loading_warning')
    loadingStore.loadingText = t('search.loading_message')
  }

  /**
   * Resets loading states
   */
  const finalizeSearch = () => {
    loadingStore.isLoading = false
    loadingStore.loadingText = ''
    loadingStore.topLoadingText = ''
  }

  /**
   * Shows appropriate user notification for flight-less conversion
   */
  const notifyFlightLessConversion = (isDefaultFlightLessPackage) => {
    if (bookStore.isFlightLessBooking && !isDefaultFlightLessPackage) {
      $toast.info(t('search.no_flights_found_converted'))
    }
  }

  /**
   * Handles search errors with appropriate user feedback
   */
  const handleSearchError = (error) => {
    if (error.response) {
      const errorData = error.response.data.error
      if (error.response.status === 422) {
        for (const [key, value] of Object.entries(errorData.errors)) {
          $toast.error(t('search.validation_error', { field: key, message: value }))
        }
        return
      }
      $toast.error(errorData.message || t('search.api_error'))
    } else {
      $toast.error(t('search.network_error'))
    }
  }

  /**
   * Checks ticket availability for all events
   */
  const checkTicketAvailability = (packageItem, payload, calculateTotalPassengers, updateOfferFailedServices) => {
    const totalPassengerNumber = calculateTotalPassengers()
    const hasOffer = payload.offerId && payload.tickets.length

    // Filter all available tickets for events
    const availableTicketsOfEvents = packageItem.events.map((event) => {
      const availableTickets = event.tickets.filter(
        ticket => ticket.availability >= totalPassengerNumber
      )
      if (!availableTickets.length) {
        throw new Error(
          t('search.insufficient_tickets_error', { count: totalPassengerNumber })
        )
      }
      return {
        ...event,
        availableTickets
      }
    })

    const bookTickets = []

    // Check if offer service has availability add it to book/tickets
    if (hasOffer) {
      const offerTickets = payload.tickets
      availableTicketsOfEvents.forEach((event) => {
        let offerTicketId = 0
        const isOfferAvailable = event.availableTickets.some(
          ticket =>
            offerTickets.includes(ticket.id) && (offerTicketId = ticket.id)
        )
        if (isOfferAvailable) {
          const ticket = {}
          const offerTicket = event.availableTickets.find(
            ticket => ticket.id === offerTicketId
          )
          ticket.ticketId = offerTicket.id
          ticket.price = offerTicket.price

          bookTickets.push({
            eventId: event.id,
            ...ticket
          })
        } else {
          updateOfferFailedServices(t('word.ticket'))
        }
      })
    }

    // Check if default service has availability add it to book/tickets otherwise add the first one
    if (!hasOffer || !bookTickets.length) {
      const defaultTickets = packageItem.defaultServices.tickets
      const defaultServiceTicketIds = defaultTickets.map(
        ticket => ticket.searchParams.ticketId
      )
      availableTicketsOfEvents.forEach((event) => {
        const isAvailableDefaultService = event.availableTickets.some(
          ticket => defaultServiceTicketIds.includes(ticket.id)
        )
        const ticket = {}
        if (!payload.offerId) {
          ticket.ticketId = event.availableTickets[0].id
          ticket.price = event.availableTickets[0].price
        }
        if (isAvailableDefaultService) {
          const defaultTicket = defaultTickets.find(
            ticket => ticket.searchParams.eventId === event.id
          )
          ticket.ticketId = defaultTicket.searchParams.ticketId
          ticket.price = defaultTicket.info.price
        }
        bookTickets.push({
          eventId: event.id,
          ...ticket
        })
      })
    }

    packageStore.events = availableTicketsOfEvents
    bookStore.tickets = bookTickets
  }

  /**
   * Checks hotel availability with optimized error handling
   */
  const checkHotelAvailability = async (packageItem, payload, hotelsSearchParams, handleHotelLessPackage, checkTwinRoomException, updateOfferFailedServices) => {
    const hasOffer = payload.offerId && payload.hotels.length

    // Since we always reset the package, we should run this again
    if (packageStore.isHotelLessPackage) {
      handleHotelLessPackage()
      return
    }

    // Update loading text for hotel search
    loadingStore.loadingText = t('search.searching_hotels')

    try {
      const resultHotelsObj = await Promise.all(
        hotelsSearchParams.map(searchParam =>
          packageStore.fetchHotels(searchParam)
        )
      )

      const bookingHotels = resultHotelsObj.map((resultHotelObj) => {
        const defaultHotel = packageItem.defaultServices.hotels.find(
          hotel => hotel.info.id === resultHotelObj.defaultHotelId
        )

        let desiredHotel = resultHotelObj.searchedHotels[0]
        if (hasOffer) {
          desiredHotel = resultHotelObj.searchedHotels.find(hotel =>
            payload.hotels.includes(hotel.id)
          )
          if (!desiredHotel) {
            desiredHotel = resultHotelObj.searchedHotels[0]
            updateOfferFailedServices(t('word.hotel'))
          }
        }

        return bookStore.createHotelBook(
          desiredHotel,
          hotelsSearchParams.find(
            searchParam =>
              searchParam.hotelId === resultHotelObj.defaultHotelId
          ),
          resultHotelObj.defaultHotelId,
          defaultHotel.info.venue,
          defaultHotel.info.orderNumber,
          defaultHotel.uniqueId
        )
      })

      bookStore.isHotelLessBooking = false
      bookStore.hotels = bookingHotels
      bookStore.baseHotels = bookingHotels
    } catch (e) {
      checkTwinRoomException()
      handleHotelLessPackage()
      throw e
    }
  }

  /**
   * Checks transit availability
   */
  const checkTransitAvailability = (packageItem, handleTransitLessPackage) => {
    if (packageStore.isTransitLessPackage) {
      handleTransitLessPackage()
      return
    }

    // Update loading text for transit
    loadingStore.loadingText = t('search.checking_transit')

    bookStore.transit = {
      info: { ...packageItem.defaultServices.transit.info },
      outwardTransitId: packageItem.defaultServices.transit.info.id,
      returnTransitId: packageItem.defaultServices.transit.info.return.id,
      outwardTimepoint: packageItem.defaultServices.transit.info.timepoint,
      returnTimepoint: packageItem.defaultServices.transit.info.return.timepoint,
      outwardTotalPrice: parseFloat(packageItem.defaultServices.transit.info.morningPrice),
      returnTotalPrice: parseFloat(packageItem.defaultServices.transit.info.return.morningPrice),
      price: parseFloat(packageItem.defaultServices.transit.info.morningPrice) + parseFloat(packageItem.defaultServices.transit.info.return.morningPrice)
    }
  }

  /**
   * Validates twin room availability
   */
  const checkTwinRoomException = (rooms, setGeneralErrorMessage) => {
    if (rooms.some(room => room.roomType === 'twin')) {
      const message = t('search.twin_room_error')
      setGeneralErrorMessage(message)
      throw new Error(message)
    }
  }

  /**
   * Handles transit-less package configuration
   */
  const handleTransitLessPackage = () => {
    packageStore.isTransitLessPackage = true
    bookStore.isTransitLessBooking = true
  }

  /**
   * Handles hotel-less package configuration
   */
  const handleHotelLessPackage = (rooms, setPassengers) => {
    const adults = rooms.reduce((total, room) => total + room.adults, 0)
    const children = rooms.reduce(
      (total, room) => total + room.children.length,
      0
    )
    setPassengers({
      adults,
      children
    })
    packageStore.isHotelLessPackage = true
    bookStore.isHotelLessBooking = true
  }

  /**
   * Orchestrates all availability checks with optimized parallel execution
   */
  const performAvailabilityCheck = async (searchParams) => {
    const {
      packageItem,
      payload,
      hotelsSearchParams,
      customOutwardFlightDate,
      customReturnFlightDate,
      isFlightMinimalSearchMode,
      calculateTotalPassengers,
      updateOfferFailedServices,
      handleHotelLessPackage: localHandleHotelLessPackage,
      handleTransitLessPackage: localHandleTransitLessPackage,
      checkTwinRoomException: localCheckTwinRoomException,
      isHotelLessPackage,
      isFlightLessPackage
    } = searchParams

    initializeSearch()

    try {
      // Step 1: Check tickets and transit (fast operations)
      checkTicketAvailability(packageItem, payload, calculateTotalPassengers, updateOfferFailedServices)
      checkTransitAvailability(packageItem, localHandleTransitLessPackage)

      // Step 2: Prepare parallel searches for slower operations
      const searchPromises = []

      // Add hotel search if needed
      if (!isHotelLessPackage) {
        searchPromises.push(
          checkHotelAvailability(
            packageItem,
            payload,
            hotelsSearchParams,
            localHandleHotelLessPackage,
            localCheckTwinRoomException,
            updateOfferFailedServices
          )
        )
      }

      // Add flight searches with progressive logic
      if (!isFlightLessPackage) {
        loadingStore.loadingText = t('search.searching_flights')

        const outwardSearchParams = {
          outwardDate: customOutwardFlightDate,
          isMinimalSearchMode: isFlightMinimalSearchMode,
          maxStops: payload.maxChanges || 0
        }

        const returnSearchParams = {
          returnDate: customReturnFlightDate,
          isMinimalSearchMode: isFlightMinimalSearchMode,
          maxStops: payload.maxChanges || 0
        }

        searchPromises.push(
          checkOutwardFlightAvailability(outwardSearchParams, payload)
        )

        searchPromises.push(
          checkReturnFlightAvailability(returnSearchParams, payload)
        )
      }

      // Execute all searches in parallel
      if (searchPromises.length > 0) {
        await Promise.all(searchPromises)
      }

      return true
    } catch (e) {
      handleSearchError(e)
      throw e
    } finally {
      finalizeSearch()
    }
  }

  return {
    // Methods
    performProgressiveFlightSearch,
    checkOutwardFlightAvailability,
    checkReturnFlightAvailability,
    checkTicketAvailability,
    checkHotelAvailability,
    checkTransitAvailability,
    checkTwinRoomException,
    handleFlightLessPackage,
    handleTransitLessPackage,
    handleHotelLessPackage,
    performAvailabilityCheck,
    initializeSearch,
    finalizeSearch,
    notifyFlightLessConversion,
    handleSearchError
  }
}
